/**
 * `/__ui/developers` — the visual-regression harness for M8-P4, and the surface
 * `tools/browser/verify-developers-local.mjs` hit-tests.
 *
 * It renders with **no session, no api and no database**, from `fixtures.ts`, because the three
 * things worth hit-testing — the 267 px rail, the two-column docs body and the playground's
 * chrome — are layout, and layout needs no data to be wrong.
 *
 *   `?screen=docs` (default) · `getting-started` · `migrating` · `playground`
 *
 * The playground's editor is **not** mounted here: GraphiQL is a lazy chunk that wants a live
 * endpoint, and a harness that needs one is not a harness. Everything around it is.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type * as React from 'react';
import { ApiDocsPage } from './ApiDocsPage';
import { GuidePage } from './GuidePage';
import { PlaygroundPage } from './PlaygroundPage';
import { createFixtureDevelopersSource } from './fixtures';
import './developers.css';

export function DevelopersHarness(): React.ReactElement {
  const screen = new URLSearchParams(window.location.search).get('screen') ?? 'docs';
  const source = createFixtureDevelopersSource();
  const client = new QueryClient({ defaultOptions: { queries: { retry: false } } });

  return (
    <QueryClientProvider client={client}>
      {screen === 'playground' ? (
        <PlaygroundPage source={source} renderEditor={false} />
      ) : screen === 'getting-started' || screen === 'migrating' ? (
        <GuidePage guideId={screen} source={source} />
      ) : (
        <ApiDocsPage source={source} />
      )}
    </QueryClientProvider>
  );
}
